import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useCompanyUsers } from '../../hooks/useCompanyData';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';
import { formatDate, formatLastActive, activityLevel } from '../../utils/time';
import { Plus, Edit, UserX, UserCheck, Search, Users } from 'lucide-react';
import './AdminUsersPage.css';

const EMPTY_FORM = { uid: '', displayName: '', email: '', password: '', role: 'production', language: 'en' };

function UserAvatar({ name }) {
  const initials = name
    ? name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  return <div className="user-avatar">{initials}</div>;
}

function ActivityDot({ level }) {
  return <span className={`activity-dot activity-dot-${level}`} />;
}

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const { users, loading } = useCompanyUsers();
  const { user: currentUser } = useAuth();
  const toast = useToast();
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit' | null
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [linkExisting, setLinkExisting] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => {
    setEditUser(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setLinkExisting(false);
    setModalMode('add');
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      uid: u.id,
      displayName: u.displayName || '',
      email: u.email || '',
      role: u.role || 'production',
      language: u.language || 'en',
    });
    setErrors({});
    setModalMode('edit');
  };

  const closeModal = () => { setModalMode(null); setEditUser(null); };

  const validate = () => {
    const e = {};
    if (!form.displayName.trim()) e.displayName = t('validation.required');
    if (modalMode === 'add') {
      if (!form.email.trim()) e.email = t('validation.required');
      if (linkExisting) {
        if (!form.uid.trim()) e.uid = t('validation.required');
      } else if (form.password.length < 6) {
        e.password = t('users.weakPassword');
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const linkExistingProfile = async () => {
    const callable = httpsCallable(functions, 'linkExistingUser');
    await callable({
      uid: form.uid.trim(),
      email: form.email.trim(),
      displayName: form.displayName.trim(),
      role: form.role,
      language: form.language,
    });
    return true;
  };

  const createUserViaFunction = async () => {
    const callable = httpsCallable(functions, 'createUser');
    await callable({
      email: form.email.trim(),
      password: form.password,
      displayName: form.displayName.trim(),
      role: form.role,
      language: form.language,
    });
    return true;
  };

  const mapCallableError = (err) => {
    if (err?.code === 'functions/already-exists') {
      setErrors((e) => ({ ...e, email: t('users.userExists'), uid: t('users.userExists') }));
      return;
    }
    if (err?.code === 'functions/not-found') {
      setErrors((e) => ({ ...e, uid: t('usersExtra.noAuthAccount') }));
      return;
    }
    if (err?.code === 'functions/invalid-argument') {
      toast(err.message || t('toast.saveFailed'), 'error');
      return;
    }
    if (err?.code === 'functions/resource-exhausted') {
      toast(err.message || t('labourRates.tooManyRequests'), 'error');
      return;
    }
    if (err?.code === 'functions/internal' || err?.code === 'functions/unavailable') {
      toast(t('users.functionUnavailable'), 'error');
      return;
    }
    toast(t('toast.saveFailed'), 'error');
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (modalMode === 'add') {
        let ok;
        if (linkExisting) {
          try {
            ok = await linkExistingProfile();
          } catch (err) {
            console.error(err);
            mapCallableError(err);
            setSaving(false);
            return;
          }
        } else {
          try {
            ok = await createUserViaFunction();
          } catch (err) {
            console.error(err);
            mapCallableError(err);
            setSaving(false);
            return;
          }
        }
        if (!ok) { setSaving(false); return; }
        toast(t('users.userCreated'), 'success');
      } else {
        await updateDoc(doc(db, COLLECTIONS.USERS, editUser.id), {
          displayName: form.displayName.trim(),
          role: form.role,
          language: form.language,
          updatedAt: serverTimestamp(),
        });
        toast(t('toast.userUpdated'), 'success');
      }
      closeModal();
    } catch (err) {
      console.error(err);
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (u) => {
    const newStatus = u.status === 'disabled' ? 'active' : 'disabled';
    try {
      await updateDoc(doc(db, COLLECTIONS.USERS, u.id), { status: newStatus, updatedAt: serverTimestamp() });
      toast(t(newStatus === 'active' ? 'toast.userEnabled' : 'toast.userDisabled'), 'success');
    } catch {
      toast(t('toast.saveFailed'), 'error');
    }
  };

  // Derived stats
  const activeCount = useMemo(
    () => users.filter((u) => u.status !== 'disabled').length,
    [users],
  );

  // Filtered + sorted list
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => {
        if (q && !u.displayName?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
        if (roleFilter && u.role !== roleFilter) return false;
        if (statusFilter) {
          if (statusFilter === 'active' && u.status === 'disabled') return false;
          if (statusFilter === 'disabled' && u.status !== 'disabled') return false;
        }
        return true;
      })
      .sort((a, b) => {
        // Current user always first
        if (a.id === currentUser?.uid) return -1;
        if (b.id === currentUser?.uid) return 1;
        return (a.displayName || '').localeCompare(b.displayName || '');
      });
  }, [users, search, roleFilter, statusFilter, currentUser]);

  const hasFilters = search || roleFilter || statusFilter;

  const clearFilters = () => { setSearch(''); setRoleFilter(''); setStatusFilter(''); };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{t('admin.userManagement')}</h1>
          {!loading && users.length > 0 && (
            <div className="users-page-meta">
              <span>{t('users.userCount_other', { count: users.length })}</span>
              <span className="users-page-meta-dot" />
              <span>{t('users.activeCount', { count: activeCount })}</span>
            </div>
          )}
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={16} />{t('users.addUser')}
        </button>
      </div>

      <div className="page-content-wide">
        <div className="card">
          {/* Toolbar */}
          {!loading && users.length > 0 && (
            <div className="users-toolbar">
              <div className="search-box">
                <Search size={14} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                <input
                  className="search-input"
                  placeholder={t('users.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                className="filter-select"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">{t('users.allRoles')}</option>
                <option value="admin">{t('users.admin')}</option>
                <option value="production">{t('users.production')}</option>
              </select>
              <select
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">{t('users.allStatuses')}</option>
                <option value="active">{t('users.enabled')}</option>
                <option value="disabled">{t('users.disabled')}</option>
              </select>
              {hasFilters && (
                <button className="btn btn-ghost btn-sm" onClick={clearFilters} style={{ flexShrink: 0 }}>
                  {t('users.clearFilters')}
                </button>
              )}
              {hasFilters && (
                <span className="users-result-count">
                  {filteredUsers.length} / {users.length}
                </span>
              )}
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="page-loading" style={{ minHeight: 200 }}>
              <div className="spinner" />
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state" style={{ padding: 'var(--space-12) var(--space-6)' }}>
              <div className="empty-state-icon"><Users size={40} strokeWidth={1.25} /></div>
              <h3>{t('common.noData')}</h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                Add users to give your team access to the app.
              </p>
              <button className="btn btn-primary" onClick={openAdd}>
                <Plus size={16} />{t('users.addUser')}
              </button>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="empty-state" style={{ padding: 'var(--space-10) var(--space-6)' }}>
              <p>{t('users.noUsersMatch')}</p>
              <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
                {t('users.clearFilters')}
              </button>
            </div>
          ) : (
            <div className="table-container" style={{ borderRadius: 0, border: 'none', boxShadow: 'none' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('users.displayName')}</th>
                    <th>{t('users.role')}</th>
                    <th className="users-col-status">{t('users.status')}</th>
                    <th>{t('users.lastActive')}</th>
                    <th className="users-col-since">{t('users.memberSince')}</th>
                    <th style={{ width: 80 }}>{t('taskTable.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    const isSelf = u.id === currentUser?.uid;
                    const isDisabled = u.status === 'disabled';
                    const rawDate = u.lastLoginAt;
                    const level = activityLevel(rawDate?.toDate?.() ?? rawDate ?? null);
                    const lastActiveLabel = rawDate
                      ? formatLastActive(rawDate?.toDate?.() ?? rawDate)
                      : null;

                    return (
                      <tr key={u.id} style={isDisabled ? { opacity: 0.6 } : undefined}>
                        {/* User cell */}
                        <td>
                          <div className="user-cell">
                            <UserAvatar name={u.displayName} />
                            <div className="user-cell-info">
                              <span className="user-cell-name">
                                {u.displayName || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{t('labourRates.unnamed')}</span>}
                                {isSelf && <span className="badge-you">{t('users.you')}</span>}
                              </span>
                              <span className="user-cell-email">{u.email}</span>
                            </div>
                          </div>
                        </td>

                        {/* Role */}
                        <td>
                          <span className={`badge badge-${u.role === 'admin' ? 'admin' : 'completed'}`}>
                            {t(`users.${u.role}`)}
                          </span>
                        </td>

                        {/* Account status */}
                        <td className="users-col-status">
                          <span className={`badge ${isDisabled ? 'badge-delay' : 'badge-active'}`}>
                            {isDisabled ? t('users.disabled') : t('users.enabled')}
                          </span>
                        </td>

                        {/* Last active */}
                        <td>
                          {lastActiveLabel ? (
                            <div className="last-active-cell">
                              <ActivityDot level={level} />
                              <span className="last-active-label">{lastActiveLabel}</span>
                            </div>
                          ) : (
                            <span className="last-active-never">{t('users.neverLoggedIn')}</span>
                          )}
                        </td>

                        {/* Member since */}
                        <td className="users-col-since text-sm text-muted">
                          {u.createdAt
                            ? formatDate(u.createdAt?.toDate?.() ?? u.createdAt)
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>
                          }
                        </td>

                        {/* Actions */}
                        <td>
                          <div className="users-actions">
                            <button
                              className="btn btn-icon btn-sm"
                              onClick={() => openEdit(u)}
                              title={t('users.editUser')}
                            >
                              <Edit size={14} />
                            </button>
                            {!isSelf && (
                              <button
                                className={`btn btn-icon btn-sm ${isDisabled ? '' : 'btn-danger-ghost'}`}
                                onClick={() => handleToggleStatus(u)}
                                title={isDisabled ? t('users.enableUser') : t('users.disableUser')}
                              >
                                {isDisabled ? <UserCheck size={14} /> : <UserX size={14} />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalMode && (
        <Modal
          title={modalMode === 'add' ? t('users.addUser') : t('users.editUser')}
          onClose={closeModal}
          footer={
            <>
              <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner spinner-sm" /> : t('common.save')}
              </button>
            </>
          }
        >
          {modalMode === 'add' && (
            <>
              <div className="form-group">
                <label className="form-label">{t('users.email')} <span className="required">*</span></label>
                <input
                  type="email"
                  className={`form-control ${errors.email ? 'is-error' : ''}`}
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  autoFocus
                />
                {errors.email && <p className="form-error">{errors.email}</p>}
              </div>

              {!linkExisting ? (
                <div className="form-group">
                  <label className="form-label">{t('users.password')} <span className="required">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.password ? 'is-error' : ''}`}
                    value={form.password}
                    onChange={(e) => set('password', e.target.value)}
                    autoComplete="new-password"
                  />
                  {errors.password && <p className="form-error">{errors.password}</p>}
                  <p className="form-hint">{t('users.passwordHint')}</p>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">{t('users.firebaseUid')} <span className="required">*</span></label>
                  <input
                    className={`form-control ${errors.uid ? 'is-error' : ''}`}
                    value={form.uid}
                    onChange={(e) => set('uid', e.target.value)}
                    placeholder={t('labourRates.uidPlaceholder')}
                  />
                  {errors.uid && <p className="form-error">{errors.uid}</p>}
                  <p className="form-hint">{t('users.firebaseUidHint')}</p>
                </div>
              )}
            </>
          )}

          <div className="form-group">
            <label className="form-label">{t('users.displayName')} <span className="required">*</span></label>
            <input
              className={`form-control ${errors.displayName ? 'is-error' : ''}`}
              value={form.displayName}
              onChange={(e) => set('displayName', e.target.value)}
            />
            {errors.displayName && <p className="form-error">{errors.displayName}</p>}
          </div>
          <div className="form-group">
            <label className="form-label">{t('users.role')}</label>
            <select className="form-control" value={form.role} onChange={(e) => set('role', e.target.value)}>
              <option value="production">{t('users.production')}</option>
              <option value="admin">{t('users.admin')}</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('users.language')}</label>
            <select className="form-control" value={form.language} onChange={(e) => set('language', e.target.value)}>
              <option value="en">{t('languages.en')}</option>
              <option value="vi">{t('languages.vi')}</option>
            </select>
          </div>

          {modalMode === 'add' && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ marginTop: 'var(--space-2)' }}
              onClick={() => { setLinkExisting((v) => !v); setErrors({}); }}
            >
              {linkExisting ? t('users.createAccountInstead') : t('users.linkExistingUid')}
            </button>
          )}
        </Modal>
      )}
    </div>
  );
}
